import './HistoryPanel.scss';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { ColumnDef } from '@tanstack/react-table';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { Modal, Typography, Loader } from '@design-system/index';
import { NotificationLine } from '@icons';
import {
    useGetNotificationHistoriesMutation,
    useGetNotificationHistoryMutation
} from '@services/product/notification/notification.endpoints';
import {
    NotificationHistoryRecipientItem,
    NotificationHistoryTable,
    NotificationTargetType
} from '@services/product/notification/notification.types';

const TARGET_LABELS: Record<NotificationTargetType, string> = {
    all: 'Tous',
    member: 'Adhérent',
    members: 'Liste d’adhérents',
    retraites: 'Retraités',
    tokens: 'Appareils ciblés'
};

const formatTarget = (type: NotificationTargetType): string => TARGET_LABELS[type] ?? type;

const formatRecipient = (recipient: NotificationHistoryRecipientItem): string => {
    const member = recipient.member;
    if (!member) {
        return recipient.token;
    }
    const pi = member.personalInformation;
    const name = [pi?.lastname, pi?.firstname].filter(Boolean).join(' ');
    const cosem = member.cosemNumber ? `${member.cosemNumber} - ` : '';
    return `${cosem}${name || `Adhérent #${member.id}`}`;
};

export const HistoryPanel = () => {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [getHistory, { data: detailResponse, isLoading: isLoadingDetail, reset }] = useGetNotificationHistoryMutation();

    useEffect(() => {
        if (selectedId !== null) {
            getHistory({ id: selectedId });
        }
    }, [selectedId, getHistory]);

    const detail = detailResponse?.data;

    const columns: ColumnDef<NotificationHistoryTable>[] = [
        {
            id: 'createdAt',
            header: 'Date',
            accessorKey: 'createdAt',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{moment(info.getValue<string>()).format('DD/MM/YYYY HH:mm')}</>
        },
        {
            id: 'title',
            header: 'Titre',
            accessorKey: 'title',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{info.getValue<string>()}</>
        },
        {
            id: 'body',
            header: 'Message',
            accessorKey: 'body',
            meta: { clickable: true },
            cell: (info) => (
                <div style={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
                    {info.getValue<string>()}
                </div>
            )
        },
        {
            id: 'targetType',
            header: 'Cible',
            accessorKey: 'targetType',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{formatTarget(info.getValue<NotificationTargetType>())}</>
        },
        {
            id: 'totalCount',
            header: 'Destinataires',
            accessorKey: 'totalCount',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{info.getValue<number>()}</>
        }
    ];

    const handleClose = () => {
        setSelectedId(null);
        reset();
    };

    return (
        <div className="notification-history-panel">
            <MutationTable
                columns={columns}
                mutationHook={useGetNotificationHistoriesMutation}
                onRowClick={(row) => setSelectedId(row.id)}
                selectable={false}
                headerTitle="Historique des notifications"
                headerIcon={<NotificationLine />}
                defaultSorting={{ field: 'createdAt', direction: 'desc' }}
            />

            <Modal
                open={selectedId !== null}
                setClose={handleClose}
                title={detail?.title ?? 'Détail de la notification'}
                className="notification-history-modal"
            >
                {isLoadingDetail || !detail ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                        <Loader />
                    </div>
                ) : (
                    <div className="notification-history-detail">
                        <div className="notification-history-detail-meta">
                            <Typography intrinsic="p">
                                <strong>Envoyée le :</strong> {moment(detail.createdAt).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                            <Typography intrinsic="p">
                                <strong>Cible :</strong> {formatTarget(detail.targetType)}
                            </Typography>
                            <Typography intrinsic="p">
                                <strong>Destinataires :</strong> {detail.totalCount}
                                {detail.errorCount > 0 ? ` (${detail.errorCount} erreur${detail.errorCount > 1 ? 's' : ''})` : ''}
                            </Typography>
                            {detail.sentBy && (
                                <Typography intrinsic="p">
                                    <strong>Envoyée par :</strong>{' '}
                                    {[detail.sentBy.firstname, detail.sentBy.lastname].filter(Boolean).join(' ') || detail.sentBy.email}
                                </Typography>
                            )}
                            {typeof detail.data?.url === 'string' && detail.data.url && (
                                <Typography intrinsic="p">
                                    <strong>Lien produit :</strong>{' '}
                                    <a
                                        href={detail.data.url as string}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#5D4B99', textDecoration: 'underline', wordBreak: 'break-all' }}
                                    >
                                        {detail.data.url as string}
                                    </a>
                                </Typography>
                            )}
                        </div>

                        <div>
                            <Typography intrinsic="h3">Message</Typography>
                            <div className="notification-history-detail-body">{detail.body}</div>
                        </div>

                        <div>
                            <Typography intrinsic="h3">
                                Destinataires ({detail.recipients?.length ?? 0})
                            </Typography>
                            <div className="notification-history-detail-recipients">
                                {(detail.recipients ?? []).map((r) => (
                                    <div key={r.id} className="notification-history-detail-recipient">
                                        <span>{formatRecipient(r)}</span>
                                        <span style={{ color: '#888', fontSize: 12 }}>{r.token.slice(0, 24)}…</span>
                                    </div>
                                ))}
                                {(!detail.recipients || detail.recipients.length === 0) && (
                                    <Typography intrinsic="p" variant="secondary">
                                        Aucun destinataire enregistré.
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
